import { DragDropContext, Droppable, type DragStart, type DropResult } from '@hello-pangea/dnd';
import { useMount, useUnmount } from 'ahooks';
import classNames from 'classnames';
import isNull from 'lodash/isNull';
import isNumber from 'lodash/isNumber';
import { useEffect, useMemo, useRef, useState } from 'react';
import { InputNumber, Scrollbars } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useFramework } from '@/framework';
import { useLocaleStore } from '@/i18n';
import type { Layer } from '@/layers/layer';
import { IWatermarkLayerProps, LayerType, type ILayerProps } from '@/version-types';
import { StageToolbar } from '../../../stage-toolbar';
import emptyPlaceholder from '../../assets/empty.png';
import { HIERARCHY_EVENTS, ITEM_HEIGHT, ITEM_MARGIN_BOTTOM_NORMAL, SCROLL_AREA_MAX_HEIGHT } from '../../constants';
import {
    deleteHierarchyLayer,
    duplicateHierarchyLayer,
    setHierarchyLayerHidden,
    setHierarchyLayerHovered,
    sortHierarchyLayerInGroup,
    sortHierarchyLayerInRoot,
    unlockHierarchyLayer,
} from '../../controller';
import { getHierarchyFrameId, onHierarchyFrameIdChange } from '../../frame-source';
import { subscribeHierarchyIconRefresh } from '../../icon-refresh';
import { getDroppableContentHeight, getLayerPath, getRenderLayers } from '../../utils';
import { Item } from '../item';
import DragPopover from './drag-popover';
import './style.less';

type Props = {
    embedded?: boolean;
    motion?: 'enter' | 'leave';
    position?: React.CSSProperties;
    onClose: () => void;
    onMotionEnd?: () => void;
};

export const Panel: React.FC<Props> = ({ embedded = false, motion, position, onClose, onMotionEnd }) => {
    const { $t } = useLocaleStore();
    const framework = useFramework();
    const { registration, flatLayer, frame, editor, events } = framework.service;
    const pluginInstance = registration.getInstance<StageToolbar>('StageToolbar');
    const { onlyShowSortableLayers, preventHiddenTypes } = pluginInstance.getHierarchyConfig();
    const isEdgelessMode = framework.service.edgelessCanvas.isEdgelessMode;

    // --- core states ---
    const [frameId, setFrameId] = useState(() => getHierarchyFrameId(framework));
    const [stageLayers, setStageLayers] = useState<ILayerProps[]>([]);
    const [iconRenderVersion, setIconRenderVersion] = useState(0);
    const [activeLayerId, setActiveLayerId] = useState<string | null>(null); // 当前面板的激活图层
    const [menuOpenLayerId, setMenuOpenLayerId] = useState<string | null>(null);
    const [groupIdStack, setGroupIdStack] = useState<string[]>([]);
    const [opacityInfo, setOpacityInfo] = useState<{ disabled: boolean; opacity: number } | null>(null);
    const groupIdStackRef = useRef<string[]>(groupIdStack);
    const frameIdRef = useRef(frameId);

    const activeLayerIdOnDragStart = useRef<string | null>(null);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const prevOpacityRef = useRef<number | null>(null);

    const renderLayerInfo = useMemo(() => {
        groupIdStackRef.current = groupIdStack;
        if (groupIdStack.length === 0) return getRenderLayers(stageLayers);

        const id = groupIdStack[groupIdStack.length - 1];
        const layer = flatLayer.getLayer(id);

        if (!layer) return getRenderLayers(stageLayers);
        return getRenderLayers(layer.children, { isChildren: true });
    }, [groupIdStack, stageLayers]);

    const groupLayerLocked = useMemo(() => {
        if (groupIdStack.length === 0) return false;

        const id = groupIdStack[0];
        const layer = flatLayer.getLayer(id);

        if (!layer) return false;
        return layer.lock;
    }, [groupIdStack]);

    const { layers, backgroundLayer, mainLayer } = renderLayerInfo;
    const disabledOpacity = opacityInfo == null || opacityInfo.disabled;

    // --- layout states ---
    const staticContentMarginTop = layers.length === 0 ? 0 : ITEM_MARGIN_BOTTOM_NORMAL;

    const rootPanelScrollAreaHeight = (() => {
        if (stageLayers.length === 0) return 0;
        const { layers: sortableLayers, backgroundLayer, mainLayer } = getRenderLayers(stageLayers);

        const droppableContentHeight = getDroppableContentHeight(sortableLayers);
        const staticContentHeight = (() => {
            if (!mainLayer && !backgroundLayer) return 0;

            let height = staticContentMarginTop;
            if (mainLayer) height = height + ITEM_HEIGHT;
            if (backgroundLayer) height = height + ITEM_MARGIN_BOTTOM_NORMAL + ITEM_HEIGHT;

            return height;
        })();

        const totalHeight = droppableContentHeight + (onlyShowSortableLayers ? 0 : staticContentHeight);
        return Math.min(totalHeight, SCROLL_AREA_MAX_HEIGHT);
    })();

    const childPanelScrollAreaHeight = (() => {
        if (groupIdStack.length === 0) return 0;
        return getDroppableContentHeight(layers);
    })();

    // 2025.8.11 产品新增交互逻辑：
    // rootPanelHeightRef 用于记录一级面板的高度，在进入子图层时，会用子图层面板的高度和 rootPanelHeightRef 进行取最大值
    // 该交互是为了防止进入子图层后高度突变导致交互不佳
    const scrollAreaHeight =
        groupIdStack.length === 0
            ? rootPanelScrollAreaHeight
            : Math.max(rootPanelScrollAreaHeight, childPanelScrollAreaHeight);

    const canBack = groupIdStack.length > 0;
    const title = canBack ? $t((v) => v.plugin.hierarchy.back) : $t((v) => v.plugin.hierarchy.title);

    // #region --- event handlers ---
    const teardown = () => {
        setStageLayers([]);
        setMenuOpenLayerId(null);
        setGroupIdStack([]);
        setActiveLayerId(null);
        setOpacityInfo(null);
        prevOpacityRef.current = null;
        editor.setDisableHover(false);
    };

    /**
     * 拉取最新图层状态
     * 1. 拉取舞台索引对应的图层列表
     * 2. 设置激活图层
     *
     * @param targetFrameId - 舞台 ID
     * @param options - 选项
     *  options.pullActiveLayer - 是否拉取激活图层
     *  options.causeBy - 引起更新的原因
     */
    const pullLatestLayerStates = (
        targetFrameId: number,
        options?: { pullActiveLayer?: boolean; causeBy?: string },
    ) => {
        const { causeBy, pullActiveLayer = true } = options || {};

        const layers = frame.getLayers(targetFrameId);
        setStageLayers(layers);

        let activeLayer: ILayerProps | null = null;

        if (pullActiveLayer) {
            const pickLayer = frame.getActivePickLayer();
            const candidate = pickLayer || frame.getActiveLayer();
            activeLayer = candidate && frame.getLayerFrameId(candidate.id) === targetFrameId ? candidate : null;
            setActiveLayerId(activeLayer ? activeLayer.id : null);
        }

        if (causeBy) {
            console.log('[hierarchy]: pullLatestLayerStates causeBy --- ', causeBy, layers);
        }

        return {
            layers,
            activeLayer,
        };
    };

    const closeMenu = () => {
        if (menuOpenLayerId) setMenuOpenLayerId(null);
    };

    const deleteLayer = (layerId: string) => {
        deleteHierarchyLayer(framework, layerId);

        setMenuOpenLayerId(null);
        if (activeLayerId === layerId) setActiveLayerId(null);
    };

    const duplicateLayer = (layerId: string) => {
        setActiveLayerId(null);
        setMenuOpenLayerId(null);
        duplicateHierarchyLayer(framework, layerId);
    };

    const unlockLayer = (layerId: string) => {
        unlockHierarchyLayer(framework, layerId);
        setActiveLayerId(layerId);
    };

    const hideLayer = (layerId: string, isHidden: boolean) => {
        const layer = setHierarchyLayerHidden(framework, layerId, isHidden, {
            syncCanvasVisibility: isEdgelessMode,
        });
        if (!layer) return;
        if (isHidden) {
            if (activeLayerId === layerId) {
                setActiveLayerId(null);
                frame.setActiveLayer('');
            }
        } else {
            if (!activeLayerId) {
                setActiveLayerId(layerId);
                frame.setActiveLayer(layerId);
            }
        }
        handleUpdateLayer(layer.id);
    };

    const handleBack = () => {
        if (groupIdStack.length === 0) return;

        const id = groupIdStack[groupIdStack.length - 1];
        const nextGroupIdStack = groupIdStack.slice(0, groupIdStack.length - 1);

        frame.setActiveLayer(id);
        setActiveLayerId(id); // ATTENTION: 这里需要手动更新 activeLayerId，因为不会触发 onActiveLayerDataUpdate（组图层已经是 activeLayer）
        setGroupIdStack(nextGroupIdStack);
    };

    const handleScroll = () => {
        closeMenu();
    };

    const enterChildrenPanel = (layer: ILayerProps) => {
        const nextGroupIdStack = [...groupIdStack, layer.id];
        setGroupIdStack(nextGroupIdStack);
        setActiveLayerId(null);
    };

    const handleSelectLayerItem = (layer: ILayerProps) => {
        closeMenu();

        // 如果图层隐藏，则不设置激活图层
        if (layer.hidden) {
            return;
        }

        if (preventHiddenTypes.includes(layer.layerType)) {
            frame.setActiveLayer('');
            events.emit(HIERARCHY_EVENTS.SELECT_MANUAL, layer);
            return;
        }

        if (activeLayerId !== layer.id) {
            frame.setActiveLayer(layer.id);

            // HACK: 如果水印图层为平铺的情况（layer.data.isFullScreen = true），将水印图层设置为激活图层
            //       不会触发 onActiveLayerDataUpdate，所以需要手动更新 activeLayerId
            if (layer.layerType === LayerType.WATERMARK && (layer.data as IWatermarkLayerProps).isFullScreen) {
                setActiveLayerId(layer.id);
            }

            events.emit(HIERARCHY_EVENTS.SELECT_MANUAL, layer);

            return;
        }

        // 激活图层为组层时，更新组层栈
        // Q: 为什么这段代码不放在监听 onActiveLayerDataUpdate 监听回调中？
        // A: 因为当组图层已经是激活图层时，再次设置为激活图层，是不会触发 onActiveLayerDataUpdate 的
        //    所以这里需要手动更新组层栈
        if (layer.layerType === LayerType.GROUP_LAYER) {
            enterChildrenPanel(layer);
        }
    };

    const handleOpenMenu = (layerId: string) => {
        if (menuOpenLayerId === layerId) {
            setMenuOpenLayerId(null);
            return;
        }
        setMenuOpenLayerId(layerId);
    };

    const handleUpdateLayer = (_: string) => {
        pullLatestLayerStates(frameId, { pullActiveLayer: false, causeBy: 'updateLayer' });
    };

    const handleDragStart = (_: DragStart) => {
        activeLayerIdOnDragStart.current = activeLayerId;
        if (activeLayerId) setActiveLayerId(null);
    };

    const handleDragEnd = (result: DropResult) => {
        const { draggableId: layerId } = result;

        const abort = () => {
            // 恢复本地 activeLayerId
            setActiveLayerId(activeLayerIdOnDragStart.current);
            activeLayerIdOnDragStart.current = null;
        };

        const layer = flatLayer.getLayer(layerId);
        if (!layer) return abort();

        const { source, destination } = result;
        if (!destination || destination.index === source.index) {
            return abort();
        }

        if (!layer.hidden) frame.setActiveLayer(layerId);
        events.emit(HIERARCHY_EVENTS.LAYER_OPERATION, layer, 'move');

        if (layer.parentId) {
            sortInGroup(layer, source.index, destination.index);
        } else {
            sortInRoot(layer, source.index, destination.index);
        }

        console.log('[hierarchy]: handleDragEnd', result);

        activeLayerIdOnDragStart.current = null;
        events.emit(HIERARCHY_EVENTS.SELECT_MANUAL, layer);

        // HACK: 全屏水印图层要特殊处理，因为不会触发 onActiveLayerDataUpdate
        if (layer.layerType === LayerType.WATERMARK && (layer.data as IWatermarkLayerProps).isFullScreen) {
            if (!layer.hidden) setActiveLayerId(layerId);
        }
    };

    /**
     * 组图层内部调整图层顺序
     *
     * @param layer - 需要调整顺序的图层
     * @param sourceIdx - 源位置
     * @param destinationIdx - 目标位置
     * @returns
     */
    const sortInGroup = (layer: ILayerProps, sourceIdx: number, destinationIdx: number) => {
        if (sortHierarchyLayerInGroup(framework, layer, sourceIdx, destinationIdx)) {
            pullLatestLayerStates(frameId, { causeBy: 'sortInGroup' });
        }
    };

    /**
     * 根图层内部调整图层顺序
     *
     * @param layer - 需要调整顺序的图层
     * @param sourceIdx - 源位置
     * @param destinationIdx - 目标位置
     * @returns
     */
    const sortInRoot = (layer: ILayerProps, sourceIdx: number, destinationIdx: number) => {
        sortHierarchyLayerInRoot(framework, {
            layer,
            stageLayers,
            sourceIndex: sourceIdx,
            destinationIndex: destinationIdx,
            frameId,
        });
    };

    const handleHoverLayer = (layerId: string, flag: boolean) => {
        setHierarchyLayerHovered(framework, layerId, flag);
    };

    const handleSetOpacity = (value: number | null, options = { writeHistory: false }) => {
        if (!activeLayerId || disabledOpacity || !isNumber(value)) return;

        const layer = flatLayer.getLayer(activeLayerId);
        if (!layer) return;

        const opacity = value / 100;
        if (!options.writeHistory) {
            registration.getInstance<Layer>('Layer').setOpacity(activeLayerId, opacity);
            if (isNull(prevOpacityRef.current)) prevOpacityRef.current = layer.opacity;
            return;
        }

        if (isNumber(prevOpacityRef.current)) {
            registration.getInstance<Layer>('Layer').setOpacity(activeLayerId, opacity, prevOpacityRef.current);
            prevOpacityRef.current = null;
        }
    };

    // #endregion --- event handlers ---

    // #region --- hooks ---

    /**
     * 初始化
     * 1. 同步舞台 ID
     * 2. 拉取图层数据
     */
    useMount(() => {
        const initialFrameId = getHierarchyFrameId(framework);
        frameIdRef.current = initialFrameId;
        setFrameId(initialFrameId);
        const { activeLayer } = pullLatestLayerStates(initialFrameId, { causeBy: 'open' });
        if (activeLayer) {
            const layerPath = getLayerPath(activeLayer, framework);
            if (layerPath.length > 1) {
                const nextGroupIdStack = layerPath.slice(0, layerPath.length - 1);
                setGroupIdStack(nextGroupIdStack);
            }
        }
    });

    /**
     * 卸载：重置状态
     */
    useUnmount(() => {
        teardown();
    });

    /**
     * [useEffect - listener]: onHierarchyFrameChange
     *
     * 普通模式跟随激活 Frame，无限画布跟随最后选中的 Frame，未选中时回到 frameId 0。
     */
    useEffect(() => {
        return onHierarchyFrameIdChange(framework, (nextFrameId) => {
            frameIdRef.current = nextFrameId;
            setFrameId(nextFrameId);
            setActiveLayerId(null);
            setMenuOpenLayerId(null);
            setGroupIdStack([]);
            setOpacityInfo(null);
            prevOpacityRef.current = null;

            pullLatestLayerStates(nextFrameId, { causeBy: 'onHierarchyFrameChange' });
        });
    }, [framework]);

    /**
     * 当前 Frame 完成绘制后只刷新图层缩略图，不重新拉取图层树。
     */
    useEffect(() => {
        return subscribeHierarchyIconRefresh({
            frame,
            events,
            getCurrentFrameId: () => frameIdRef.current,
            onRefresh: () => setIconRenderVersion((version) => version + 1),
        });
    }, [events, frame]);

    /**
     * [useEffect - listener]: onRerenderStage
     *
     * 监听舞台 re-render，同步图层数据
     * 删除/新增图层时，会触发 onRerenderStage
     */
    useEffect(() => {
        const update = () => {
            const { layers } = pullLatestLayerStates(frameIdRef.current, {
                pullActiveLayer: false,
                causeBy: 'onRerenderStage',
            });

            // 子图层被删除/解组后当前组可能退化，需要回退到上一级
            if (groupIdStackRef.current.length === 0) return;

            const isExist = layers.some(
                (layer) => layer.id === groupIdStackRef.current[groupIdStackRef.current.length - 1],
            );

            if (!isExist) {
                setGroupIdStack(groupIdStackRef.current.slice(0, groupIdStackRef.current.length - 1));
            }
        };
        const sortListener = frame.onSortLayer((sortedFrameId) => {
            if (sortedFrameId !== frameIdRef.current) return;
            update();
        });
        const listener = framework.service.flatLayer.onChange((_layerId, _layer, args) => {
            if (!args.isAdd && !args.isDelete) return;
            update();
        });

        return () => {
            events.off(listener);
            events.off(sortListener);
        };
    }, []);

    /**
     * [useEffect - listener]: onActiveLayerDataUpdate
     *
     * 监听激活图层变化，维护 activeLayerId
     * 需要根据以下两种场景进行处理：
     * 1. 更新的是一级图层
     * 2. 更新的是组图层内的图层
     */
    useEffect(() => {
        const listener = frame.onActiveLayerDataUpdate((_activeLayer, pickLayer) => {
            const activeLayer = pickLayer || _activeLayer;

            if (!activeLayer) return setActiveLayerId(null);
            if (frame.getLayerFrameId(activeLayer.id) !== frameIdRef.current) return setActiveLayerId(null);

            const layerId = activeLayer.id;
            const layerType = activeLayer.layerType;
            const parentId = activeLayer.parentId;
            const currentFrameId = frameIdRef.current;

            // case 1: 更新的是一级图层
            if (!parentId) {
                // case 1.1: 此时面板显示组图层内的图层，但是更新的是一级图层的组图层
                // 此时不能直接将 groupIdStack 置空，否则面板会直接跳回一级页面
                if (layerType === LayerType.GROUP_LAYER && groupIdStack.length > 0) {
                    pullLatestLayerStates(currentFrameId, {
                        pullActiveLayer: false,
                        causeBy: 'onActiveLayerDataUpdate - onRoot1',
                    });
                    setActiveLayerId(null);
                    setGroupIdStack([...groupIdStack]); // 触发 re-render, 更新 groupLayerLocked 状态
                    return;
                }

                // case 1.2: 此时面板显示一级图层
                if (layerId !== activeLayerId) setActiveLayerId(layerId);
                if (groupIdStack.length > 0) setGroupIdStack([]);

                pullLatestLayerStates(currentFrameId, {
                    pullActiveLayer: false,
                    causeBy: 'onActiveLayerDataUpdate - onRoot2',
                });
                return;
            }

            // case 2: 更新的是组图层内的图层
            if (layerId !== activeLayerId) setActiveLayerId(layerId);

            if (groupIdStack.length === 0 || groupIdStack[groupIdStack.length - 1] !== parentId) {
                const layerPath = getLayerPath(activeLayer, framework);
                if (layerPath.length > 1) {
                    const nextGroupIdStack = layerPath.slice(0, layerPath.length - 1);
                    setGroupIdStack(nextGroupIdStack);
                }
            }

            pullLatestLayerStates(currentFrameId, {
                pullActiveLayer: false,
                causeBy: 'onActiveLayerDataUpdate - inGroup',
            });
        });

        return () => {
            events.off(listener);
        };
    }, [activeLayerId, groupIdStack.length]);

    /**
     * [useEffect - listener]: 监听内部 LAYER_OPERATION 事件
     */
    useEffect(() => {
        const listener = events.on(HIERARCHY_EVENTS.LAYER_OPERATION, (layer: ILayerProps, op: string) => {
            if (op === 'unlock') {
                return unlockLayer(layer.id);
            }

            setMenuOpenLayerId(null);

            if (op === 'delete') {
                return deleteLayer(layer.id);
            }

            if (op === 'duplicate') {
                return duplicateLayer(layer.id);
            }

            if (op === 'hide') {
                return hideLayer(layer.id, true);
            }

            if (op === 'show') {
                return hideLayer(layer.id, false);
            }
        });

        return () => {
            events.off(listener);
        };
    }, [activeLayerId]);

    useEffect(() => {
        const stopWheelPropagation = (event: WheelEvent) => event.stopPropagation();
        scrollAreaRef.current?.addEventListener('wheel', stopWheelPropagation);
        return () => scrollAreaRef.current?.removeEventListener('wheel', stopWheelPropagation);
    }, []);

    useEffect(() => {
        if (!isEdgelessMode) return;
        const listener = events.on(HIERARCHY_EVENTS.SET_OBJECT_TO_CENTER, (layer: ILayerProps) => {
            framework.service.edgelessCanvas.setObjectToCenterByLayerId(layer.id);
        });
        return () => events.off(listener);
    }, [events, framework, isEdgelessMode]);

    useEffect(() => {
        if (!isEdgelessMode) {
            setOpacityInfo(null);
            return;
        }

        const updateOpacity = (layer: ILayerProps | null) => {
            if (!layer || frame.getLayerFrameId(layer.id) !== frameIdRef.current) {
                setOpacityInfo(null);
                return;
            }
            const disabledLayers = [LayerType.GROUP_LAYER, LayerType.MEDIA_LAYER];
            setOpacityInfo({
                disabled: disabledLayers.includes(layer.layerType),
                opacity: layer.opacity,
            });
        };

        updateOpacity(activeLayerId ? flatLayer.getLayer(activeLayerId) : null);
        const listener = frame.onActiveLayerDataUpdate((activeLayer, pickLayer) => {
            updateOpacity(pickLayer || activeLayer);
        });
        return () => events.off(listener);
    }, [activeLayerId, events, flatLayer, frame, isEdgelessMode]);

    // #endregion --- hooks ---

    const renderContent = () => {
        if (onlyShowSortableLayers && layers.length === 0) {
            return (
                <div className="hierarchy-panel__empty">
                    <img src={emptyPlaceholder} alt="empty" />
                    <span>{$t((v) => v.plugin.hierarchy.empty)}</span>
                </div>
            );
        }

        return (
            <div ref={scrollAreaRef} className="hierarchy-panel__body" style={{ height: scrollAreaHeight }}>
                <Scrollbars autoHide onScroll={handleScroll}>
                    <>
                        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
                            <Droppable droppableId="hierarchy">
                                {(provided) => (
                                    <div
                                        className="hierarchy-panel__content"
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        style={groupIdStack.length === 0 ? {} : { minHeight: '100%' }} // 防止在子面板时，拖拽区域的高度过小导致交互不佳
                                    >
                                        {layers.map((layer, idx) => {
                                            const cls = classNames('hierarchy-panel__item', {
                                                'hierarchy-panel__item--grouped':
                                                    layer.layerType === LayerType.GROUP_LAYER,
                                            });

                                            const ancestorLayerLocked =
                                                groupIdStack.length > 0 &&
                                                layer.parentId === groupIdStack[groupIdStack.length - 1] &&
                                                groupLayerLocked;

                                            return (
                                                <Item
                                                    key={layer.id}
                                                    className={cls}
                                                    index={idx}
                                                    layer={layer}
                                                    iconRenderVersion={iconRenderVersion}
                                                    ancestorLayerLocked={ancestorLayerLocked}
                                                    active={activeLayerId === layer.id}
                                                    menuOpen={menuOpenLayerId === layer.id}
                                                    onClick={() => handleSelectLayerItem(layer)}
                                                    onHover={(flag) => handleHoverLayer(layer.id, flag)}
                                                    onOpenMenu={() => handleOpenMenu(layer.id)}
                                                    onUpdate={() => handleUpdateLayer(layer.id)}
                                                    onCloseMenu={closeMenu}
                                                />
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        {(backgroundLayer || mainLayer) && !onlyShowSortableLayers ? (
                            <div
                                className="hierarchy-panel__content hierarchy-panel__content--static"
                                style={{ marginTop: staticContentMarginTop }}
                            >
                                {mainLayer ? (
                                    <Item
                                        key={mainLayer.id}
                                        className="hierarchy-panel__item"
                                        index={-1}
                                        layer={mainLayer}
                                        iconRenderVersion={iconRenderVersion}
                                        active={activeLayerId === mainLayer.id}
                                        menuOpen={menuOpenLayerId === mainLayer.id}
                                        onClick={() => handleSelectLayerItem(mainLayer)}
                                        onUpdate={() => handleUpdateLayer(mainLayer.id)}
                                    />
                                ) : null}

                                {backgroundLayer ? (
                                    <Item
                                        key={backgroundLayer.id}
                                        className="hierarchy-panel__item"
                                        index={-1}
                                        layer={backgroundLayer}
                                        iconRenderVersion={iconRenderVersion}
                                        active={activeLayerId === backgroundLayer.id}
                                        menuOpen={menuOpenLayerId === backgroundLayer.id}
                                        onClick={() => handleSelectLayerItem(backgroundLayer)}
                                        onUpdate={() => handleUpdateLayer(backgroundLayer.id)}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </>
                </Scrollbars>
            </div>
        );
    };

    // 不要在 Panel 根节点阻止 mousedown：独立宿主需要事件冒泡到 DraggableCore 才能开始拖动。
    // 图层内容区由 DragPopover 的 cancelClassName 排除，仍用于图层排序而不是移动面板。
    const panel = (
        <div
            className={classNames('hierarchy-panel', {
                'hierarchy-panel--fixed': embedded,
            })}
            onContextMenu={(event) => {
                event.stopPropagation();
                event.preventDefault();
            }}
            onMouseEnter={() => editor.setDisableHover(true)}
            onMouseLeave={() => editor.setDisableHover(false)}
        >
            <header className="hierarchy-panel__header">
                <div
                    className={classNames('hierarchy-panel__title', {
                        'hierarchy-panel__title--clickable': canBack,
                    })}
                    onClick={handleBack}
                >
                    {canBack ? (
                        <span className="hierarchy-panel__back">
                            <IconFont name="BackArrowBlack" fontSize={14} />
                        </span>
                    ) : null}
                    {title}
                </div>

                {isEdgelessMode ? (
                    <div
                        className={classNames('hierarchy-panel__opacity', {
                            'hierarchy-panel__opacity--disabled': disabledOpacity,
                        })}
                    >
                        <div className="hierarchy-panel__opacity-label">{$t((value) => value.Title_Opacity)}</div>
                        <div className="hierarchy-panel__opacity-input">
                            <InputNumber
                                min={0}
                                max={100}
                                disabled={disabledOpacity}
                                enableMouseWheel={false}
                                showStepBtn={false}
                                value={opacityInfo == null ? 100 : Math.round(opacityInfo.opacity * 100)}
                                addonAfter={<div className="hierarchy-panel__opacity-unit">%</div>}
                                onChange={(value: number | null) => handleSetOpacity(value)}
                                onChangeAfter={(value: number | null) =>
                                    handleSetOpacity(value, { writeHistory: true })
                                }
                            />
                        </div>
                    </div>
                ) : null}

                {/* 无限画布沿用合并前的透明度头部，关闭操作由图层 Trigger 或工具栏状态负责。 */}
                {!isEdgelessMode ? (
                    <div className="hierarchy-panel__close" onClick={onClose}>
                        <IconFont name="CrossBold" fontSize={12} />
                    </div>
                ) : null}
            </header>

            {renderContent()}
        </div>
    );

    // embedded 由工具栏 Popover 提供定位，固定渲染且不创建 Portal/拖动宿主。
    if (embedded) return panel;

    // 独立 Panel 挂载到 body，避免被编辑器视口裁剪；桌面端可拖动，iPad 由 DragPopover 固定。
    return (
        <DragPopover
            handleClassName="hierarchy-panel"
            cancelClassName="hierarchy-panel__content"
            initialPosition={position}
            motion={motion}
            onMotionEnd={onMotionEnd}
        >
            {panel}
        </DragPopover>
    );
};
